import { IconButton, Popover, Typography, useTheme } from '@mui/material'
import {
    casOpenBg,
    casTextColor,
    green,
    marketStatusBlue,
    marketStatusOpenBg,
    postMarketOpenBg,
    preMarketOpenBg,
    preMarketTextColor
} from '../../assets/colors'
import Morning from '../../assets/icons/Morning'
import MarketOpen from '../../assets/icons/MarketOpen'
import MarketClosed from '../../assets/icons/MarketClosed'
import PostClose from '../../assets/icons/PostClose'
import { TradingSession } from '../../proto/generated/trading-calendar'
import MarketStatusBar from './Slider'
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { selectPrimarySecurity } from '../../selectors/home'
import { NonInteractiveTooltip } from '../../components/custom'
import { selectExchangeTradingSessions, selectTradingCalendarDataByExchange } from '../../selectors/chart'
import { darkHoverBtnColor, lightHoverBtnColor } from '../../theme/theme'
import { constructCalendarDataLookup, getTradingSessionsForDate } from '../../util/chart'
import { toDayStart } from '../../util/dateTime'
import { getTradingSessionsForSecurity } from '../../util/tradingSessionSelection'

const DAY_MS = 24 * 60 * 60 * 1000

// Calendar template sessions store times as seconds-from-midnight; anchor them to a concrete day (epoch ms).
const anchorTemplateToDay = (dayMs: number, template: TradingSession): TradingSession => ({
    ...template,
    preOpenStartTime: dayMs + (template.preOpenStartTime || 0) * 1000,
    preOpenEndTime: dayMs + (template.preOpenEndTime || 0) * 1000,
    startTime: dayMs + (template.startTime || 0) * 1000,
    endTime: dayMs + (template.endTime || 0) * 1000,
    postCloseStartTime: dayMs + (template.postCloseStartTime || 0) * 1000,
    postCloseEndTime: dayMs + (template.postCloseEndTime || 0) * 1000,
    casStartTime: template.casStartTime ? dayMs + template.casStartTime * 1000 : 0,
    casEndTime: template.casEndTime ? dayMs + template.casEndTime * 1000 : 0
})

const MarketStatus = () => {
    const theme = useTheme()
    const closedBg = theme.palette.mode === 'dark' ? darkHoverBtnColor : lightHoverBtnColor
    const { expressionMode, primary, underlying, expressionSecurities } = useSelector(selectPrimarySecurity)
    const mainSecurity = expressionMode ? expressionSecurities[0] : (underlying || primary)
    const exchangeTradingSessions = useSelector(selectExchangeTradingSessions)
    const tradingSessions = useMemo(
        () => getTradingSessionsForSecurity(exchangeTradingSessions, mainSecurity),
        [exchangeTradingSessions, mainSecurity?.exchange, mainSecurity?.segment, mainSecurity?.partOfCAS]
    )
    const tradingCalendarData = useSelector(selectTradingCalendarDataByExchange(mainSecurity?.exchange))
    const calendarData = useMemo(
        () => constructCalendarDataLookup(tradingCalendarData, mainSecurity),
        [tradingCalendarData, mainSecurity?.exchange, mainSecurity?.segment, mainSecurity?.partOfCAS]
    )
    const [marketOpen, setMarketOpen] = useState<boolean>()
    const [preMarketOpen, setPreMarketOpen] = useState<boolean>()
    const [casOpen, setCasOpen] = useState<boolean>()
    const [postMarketOpen, setPostMarketOpen] = useState<boolean>()
    const [holiday, setHoliday] = useState<boolean>()
    const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null)
    const [message, setMessage] = useState<{ lead: string; highlight: string; trail?: string }>()
    const [displaySession, setDisplaySession] = useState<TradingSession>()
    const open = Boolean(anchorEl)
    const [updatedKey, setUpdatedKey] = useState<number>(0)

    const nextTradingDayStartMs = useCallback((nowMs: number) => {
        for (let d = 1; d <= 10; d++) {
            const dayMs = toDayStart(nowMs).getTime() + d * DAY_MS
            const sessions = getTradingSessionsForDate(Math.floor(dayMs / 1000), calendarData)
            if (sessions && sessions.length) return dayMs
        }
        return undefined
    }, [calendarData])

    // Epoch ms of the next pre-open: prefer the live exchange sessions, else derive from the trading calendar.
    const computeNextPreOpenMs = useCallback((nowMs: number, sessions: TradingSession[]): number | undefined => {
        const futurePreOpens = sessions.map((s) => s.preOpenStartTime).filter((t) => t > nowMs)
        if (futurePreOpens.length) return Math.min(...futurePreOpens)
        const dayMs = toDayStart(nowMs).getTime()
        const todayTemplate = getTradingSessionsForDate(Math.floor(dayMs / 1000), calendarData)?.[0]
        if (todayTemplate) {
            const pre = dayMs + ((todayTemplate.preOpenStartTime || todayTemplate.startTime) || 0) * 1000
            if (pre > nowMs) return pre
        }
        const nextDayMs = nextTradingDayStartMs(nowMs)
        if (nextDayMs) {
            const nextTemplate = getTradingSessionsForDate(Math.floor(nextDayMs / 1000), calendarData)?.[0]
            if (nextTemplate) return nextDayMs + ((nextTemplate.preOpenStartTime || nextTemplate.startTime) || 0) * 1000
        }
        return undefined
    }, [calendarData, nextTradingDayStartMs])

    // A session to draw the slider for when the live exchange data has none (e.g. weekends/holidays).
    const buildCalendarSession = useCallback((nowMs: number): TradingSession | undefined => {
        const dayMs = toDayStart(nowMs).getTime()
        const todayTemplate = getTradingSessionsForDate(Math.floor(dayMs / 1000), calendarData)?.[0]
        if (todayTemplate) return anchorTemplateToDay(dayMs, todayTemplate)
        // Non-trading day: collapse all windows to midnight so the bar renders as an empty (void) day.
        return {
            type: '',
            name: '',
            startTime: dayMs,
            endTime: dayMs,
            preOpenStartTime: dayMs,
            preOpenEndTime: dayMs,
            postCloseStartTime: dayMs,
            postCloseEndTime: dayMs
        }
    }, [calendarData])

    const handleClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget)
    }, [])

    const handleClose = useCallback(() => {
        setAnchorEl(null)
    }, [])

    useEffect(() => {
        if (!tradingSessions.length || !mainSecurity) return
        const timeoutIds: NodeJS.Timeout[] = []
        const nowTime = Date.now()
        for (let i = 0; i < tradingSessions.length; i++) {
            const tradingSession = tradingSessions[i]
            for (const boundary of [
                tradingSession.preOpenStartTime,
                tradingSession.preOpenEndTime,
                tradingSession.startTime,
                tradingSession.endTime,
                tradingSession.casStartTime,
                tradingSession.casEndTime,
                tradingSession.postCloseStartTime,
                tradingSession.postCloseEndTime
            ]) {
                if (boundary && nowTime < boundary) {
                    timeoutIds.push(setTimeout(() => {
                        setUpdatedKey(prevKey => prevKey + 1)
                    }, boundary - nowTime))
                }
            }
        }
        return () => {
            timeoutIds.forEach(id => clearTimeout(id))
        }
    }, [tradingSessions, mainSecurity])

    const formatDuration = (durationMs: number) => {
        const totalSeconds = Math.max(0, Math.floor(durationMs / 1000))
        const days = Math.floor(totalSeconds / 86400)
        const hours = Math.floor((totalSeconds % 86400) / 3600)
        const minutes = Math.floor((totalSeconds % 3600) / 60)
        const seconds = totalSeconds % 60
        const part = (value: number, unit: string) => `${value} ${unit}${value === 1 ? '' : 's'}`
        if (totalSeconds >= 86400) return `${part(days, 'day')} and ${part(hours, 'hour')}`
        if (totalSeconds >= 3600) return `${part(hours, 'hour')} and ${part(minutes, 'minute')}`
        if (totalSeconds >= 300) return part(minutes, 'minute')
        if (minutes === 0) return part(seconds, 'second')
        return `${part(minutes, 'minute')} and ${part(seconds, 'second')}`
    }

    useEffect(() => {
        const nowMs = Date.now()
        if (!mainSecurity) return
        const sessions = tradingSessions
        let marketOpen = false
        let preMarketOpen = false
        let casOpen = false
        let postMarketOpen = false
        let holiday = false
        let message: { lead: string; highlight: string; trail?: string } = { lead: '', highlight: '' }
        let activeSession: TradingSession | undefined

        for (const tradingSession of sessions) {
            const hasCas = !!(tradingSession.casStartTime && tradingSession.casEndTime)
            if (nowMs >= tradingSession.preOpenStartTime && nowMs <= tradingSession.preOpenEndTime) {
                preMarketOpen = true
                activeSession = tradingSession
                message = {
                    lead: 'The market is open for pre-market session.',
                    highlight: `Regular trading session will start in ${formatDuration(tradingSession.startTime - nowMs)}.`
                }
                break
            }
            if (nowMs >= tradingSession.startTime && nowMs <= tradingSession.endTime) {
                marketOpen = true
                activeSession = tradingSession
                const remainingMs = tradingSession.endTime - nowMs
                if (remainingMs < 30 * 60 * 1000) {
                    message = {
                        lead: 'Regular trading session ends in',
                        highlight: `${formatDuration(remainingMs)}.`,
                        trail: hasCas
                            ? ' Closing auction will follow.'
                            : ' The post-close session will start after a 10-minute break.'
                    }
                } else {
                    message = {
                        lead: 'Regular trading session is active.',
                        highlight: `It will end in ${formatDuration(remainingMs)}.`
                    }
                }
                break
            }
            if (hasCas && nowMs >= tradingSession.casStartTime && nowMs <= tradingSession.casEndTime) {
                casOpen = true
                activeSession = tradingSession
                message = {
                    lead: 'Closing auction session is active.',
                    highlight: `It will end in ${formatDuration(tradingSession.casEndTime - nowMs)}.`
                }
                break
            }
            if (hasCas && nowMs > tradingSession.endTime && nowMs < tradingSession.casStartTime) {
                casOpen = true
                activeSession = tradingSession
                message = {
                    lead: 'Regular trading session has ended.',
                    highlight: `Closing auction begins in ${formatDuration(tradingSession.casStartTime - nowMs)}.`
                }
                break
            }
            if (nowMs > tradingSession.endTime && nowMs < tradingSession.postCloseStartTime
                && !(hasCas && nowMs <= tradingSession.casEndTime)) {
                postMarketOpen = true
                activeSession = tradingSession
                message = {
                    lead: 'Regular trading session has ended.',
                    highlight: `Post-close session begins in ${formatDuration(tradingSession.postCloseStartTime - nowMs)}.`
                }
                break
            }
            if (nowMs >= tradingSession.postCloseStartTime && nowMs <= tradingSession.postCloseEndTime) {
                postMarketOpen = true
                activeSession = tradingSession
                message = {
                    lead: 'Post-close session is active.',
                    highlight: `It will end in ${formatDuration(tradingSession.postCloseEndTime - nowMs)}.`
                }
                break
            }
        }

        if (!activeSession) {
            const nextPreOpenMs = computeNextPreOpenMs(nowMs, sessions)
            const openDuration = nextPreOpenMs ? formatDuration(nextPreOpenMs - nowMs) : ''
            // Trading holiday: a weekday with no scheduled sessions in the calendar (weekends are not "holidays").
            const dayMs = toDayStart(nowMs).getTime()
            const weekday = new Date(nowMs).getDay()
            const isWeekend = weekday === 0 || weekday === 6
            const todaySessions = getTradingSessionsForDate(Math.floor(dayMs / 1000), calendarData)
            holiday = !isWeekend && (!todaySessions || todaySessions.length === 0)
            message = holiday
                ? { lead: 'No trading sessions are scheduled today.', highlight: nextPreOpenMs ? `Market will open for pre-market trading in ${openDuration}.` : '' }
                : { lead: 'Market is closed.', highlight: nextPreOpenMs ? `It'll open for pre-market trading in ${openDuration}.` : '' }
        }

        const todayStr = new Date(nowMs).toDateString()
        const sameDaySession = sessions.find((s) => new Date(s.startTime).toDateString() === todayStr)
        const nextFutureSession = sessions
            .filter((s) => s.preOpenStartTime > nowMs)
            .sort((a, b) => a.preOpenStartTime - b.preOpenStartTime)[0]

        setMarketOpen(marketOpen)
        setPreMarketOpen(preMarketOpen)
        setCasOpen(casOpen)
        setPostMarketOpen(postMarketOpen)
        setHoliday(holiday)
        setMessage(message)
        setDisplaySession(activeSession || sameDaySession || nextFutureSession || sessions[0] || buildCalendarSession(nowMs))
    }, [tradingSessions, mainSecurity, updatedKey, computeNextPreOpenMs, buildCalendarSession, calendarData])

    useEffect(() => {
        if (!open) return
        setUpdatedKey((prev) => prev + 1)
        const id = setInterval(() => setUpdatedKey((prev) => prev + 1), 1000)
        return () => clearInterval(id)
    }, [open])

    const statusLabel = preMarketOpen
        ? 'Pre-market is open'
        : marketOpen
            ? 'Market Open'
            : casOpen
                ? 'Closing auction is open'
                : postMarketOpen
                    ? 'Post-close is open'
                    : holiday
                        ? 'Market Holiday'
                        : 'Market Closed'
    const statusTitle = preMarketOpen
        ? 'Pre-market'
        : marketOpen
            ? 'Market open'
            : casOpen
                ? 'Closing auction'
                : postMarketOpen
                    ? 'Post-close'
                    : holiday
                        ? 'Market holiday'
                        : 'Market closed'
    const statusBg = preMarketOpen
        ? preMarketOpenBg
        : marketOpen
            ? marketStatusOpenBg
            : casOpen
                ? casOpenBg
                : postMarketOpen
                    ? postMarketOpenBg
                    : closedBg
    const statusColor = preMarketOpen
        ? preMarketTextColor
        : marketOpen
            ? green
            : casOpen
                ? casTextColor
                : postMarketOpen
                    ? marketStatusBlue
                    : 'text.secondary'
    const statusIcon = preMarketOpen
        ? <Morning />
        : marketOpen || casOpen
            ? <MarketOpen />
            : postMarketOpen
                ? <PostClose />
                : <MarketClosed />

    const tooltipContent = useMemo(() => (
        <Typography variant="caption">{statusLabel}</Typography>
    ), [statusLabel])

    const tooltipChildren = useMemo(() => {
        return <IconButton
            style={{ padding: '0px' }}
            disableRipple={true}
            data-event={'Market Status Icon Clicked'}
            data-event-source={'stockbar'}
            onClick={handleClick}>
            <div
                style={{
                    display: 'flex',
                    borderRadius: '50%',
                    color: theme.palette.text.secondary,
                    backgroundColor: statusBg
                }}>
                {statusIcon}
            </div>
        </IconButton>
    }, [handleClick, statusBg, statusIcon, theme.palette.text.secondary])

    const popover = useMemo(() => (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            style={{ marginLeft: '-16px', marginTop: '8px' }}
            slotProps={{
                paper: {
                    style: { borderRadius: '6px' }
                }
            }}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left'
            }}
        >
            <div style={{ width: '340px', maxWidth: '340px' }}>
                <div style={{
                    display: 'flex',
                    paddingTop: '12px',
                    paddingBottom: '12px',
                    paddingLeft: '16px',
                    paddingRight: '28px'
                }}>
                    <div
                        style={{
                            display: 'flex',
                            alignSelf: 'flex-start',
                            padding: '0px',
                            borderRadius: '50%',
                            color: theme.palette.text.secondary,
                            backgroundColor: statusBg,
                            marginTop: '4px'
                        }}>
                        {statusIcon}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', marginLeft: '8px' }}>
                        <Typography variant="body1"
                                    style={{ fontWeight: 600 }}
                                    sx={{ color: statusColor }}>{statusTitle}</Typography>
                        <Typography variant="body2" sx={{ marginTop: '2px' }}>
                            {message?.lead}{message?.lead ? ' ' : ''}
                            <Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>{message?.highlight}</Typography>
                            {message?.trail}
                        </Typography>
                        <div style={{
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            marginTop: '16px'
                        }}>
                            {displaySession && <MarketStatusBar tradingSession={displaySession} />}
                        </div>
                    </div>
                </div>
            </div>
        </Popover>
    ), [open, anchorEl, handleClose, statusBg, statusIcon, statusColor, statusTitle, message, displaySession, theme.palette.text.secondary])

    return (<>
        <NonInteractiveTooltip title={tooltipContent} arrow={true} placement="top">
            {tooltipChildren}
        </NonInteractiveTooltip>
        {popover}
    </>)
}
export default memo(MarketStatus)